import { useEffect } from 'react';
import {
  Mail,
  Smartphone,
  MessageSquare,
  Bell,
  CreditCard,
  Camera,
  Cloud,
  CheckCircle,
  Settings,
  Shield,
} from 'lucide-react';
import type { IntegrationType } from '@yawi/types';
import { providersByType, configFieldsByProvider, typeInfo } from '../providerConfig';
import type { StepProps } from '../types';

const typeIcons: Record<IntegrationType, React.ReactNode> = {
  EMAIL: <Mail className="w-5 h-5" />,
  SMS: <Smartphone className="w-5 h-5" />,
  WHATSAPP: <MessageSquare className="w-5 h-5" />,
  PUSH: <Bell className="w-5 h-5" />,
  PAYMENT: <CreditCard className="w-5 h-5" />,
  ACCESS: <Camera className="w-5 h-5" />,
  STORAGE: <Cloud className="w-5 h-5" />,
};

export function SummaryStep({ data, onValidChange }: StepProps) {
  const providerInfo = providersByType[data.type].find(p => p.provider === data.provider);
  const info = typeInfo[data.type];
  const fields = configFieldsByProvider[data.provider] || [];

  useEffect(() => {
    onValidChange(true);
  }, [onValidChange]);

  const getFieldLabel = (key: string) => {
    const field = fields.find(f => f.key === key);
    return field?.label || key;
  };

  const isSecretField = (key: string) => {
    const field = fields.find(f => f.key === key);
    return field?.type === 'password';
  };

  const maskValue = (value: string) => {
    if (value.length <= 8) return '••••••••';
    return value.substring(0, 4) + '••••' + value.substring(value.length - 4);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Resumen de la integración
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Revisa la configuración antes de guardar
        </p>
      </div>

      {/* Integration Overview */}
      <div className="bg-gradient-to-br from-brand-50 to-brand-100 dark:from-brand-900/20 dark:to-brand-900/10 rounded-xl p-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            <div className="text-brand-600 dark:text-brand-400">
              {typeIcons[data.type]}
            </div>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
              {data.name || 'Sin nombre'}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {info.label} • {providerInfo?.name}
            </p>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-4">
        {/* Type & Provider */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Tipo
              </span>
            </div>
            <p className="text-gray-900 dark:text-white font-medium">{info.label}</p>
          </div>

          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Proveedor
              </span>
            </div>
            <p className="text-gray-900 dark:text-white font-medium">{providerInfo?.name}</p>
          </div>
        </div>

        {/* Priority */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Prioridad
              </span>
              <p className="text-gray-900 dark:text-white font-medium">
                {data.priority === 0 ? 'Primaria (se usa primero)' : `Fallback ${data.priority} (respaldo)`}
              </p>
            </div>
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
              data.priority === 0
                ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400'
                : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
            }`}>
              {data.priority === 0 ? 'Principal' : `Fallback #${data.priority}`}
            </span>
          </div>
        </div>

        {/* Configuration */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Configuración
            </span>
          </div>
          <div className="space-y-2">
            {Object.entries(data.config).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between py-1">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {getFieldLabel(key)}
                </span>
                <span className="text-sm font-mono text-gray-900 dark:text-white">
                  {isSecretField(key)
                    ? maskValue(String(value))
                    : typeof value === 'boolean'
                      ? (value ? 'Sí' : 'No')
                      : String(value)
                  }
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Confirmation */}
      <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-emerald-800 dark:text-emerald-200">
              Listo para guardar
            </h4>
            <p className="text-sm text-emerald-600 dark:text-emerald-300 mt-1">
              Haz clic en "Guardar" para crear la integración. Podrás probar la conexión después.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
